import React from 'react';
import { ArrowRight } from 'react-feather';
import { CALENDAR_LINK } from '../../constants/demo-link';
import styled from '../../styled';
import { Headline } from '../Headline';
import { NewBadge } from '../NewBadge';

const SectionWrapper = styled('section')`
  padding: 100px 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 960px) {
    padding: 80px 24px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 60px;

  @media (max-width: 960px) {
    margin-bottom: 40px;
  }
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin-top: 16px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  background-color: #fff;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const SolutionCard = styled('div')<{ new: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ImagePlaceholder = styled('div')`
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: #e2e8f0;
  border-radius: 12px;
  margin-bottom: 8px;
`;

const SolutionTitle = styled('h3')`
  font-size: 24px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const SolutionDescription = styled('p')`
  font-size: 16px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
`;

const PerfectFor = styled('div')`
  margin-top: 8px;
`;

const PerfectForLabel = styled('p')`
  font-size: 14px;
  font-weight: 600;
  color: #4f4f4f;
  margin: 0 0 12px 0;
`;

const TeamList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TeamItem = styled('li')`
  font-size: 14px;
  color: #4f4f4f;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #ff6b35;
    font-weight: bold;
  }
`;

const LearnMoreLink = styled('a')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #ff6b35;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;
  width: fit-content;
  margin-top: 8px;

  &:hover {
    opacity: 0.8;
  }
`;

const solutions = [
  {
    title: 'Unify',
    description:
      'Aggregate and unify affiliate data from 100+ networks into trusted revenue intelligence built to grow revenue and confidence. Purchases, performance, rates, payouts, and beyond.',
    teams: [
      'To close more brand deals',
      'Centralized insights and analytics',
      'Streamlined affiliate data management',
      'Confident financial reconciliation',
      'Modern APIs to power linking, creator analytics, user attribution and payouts',
      'Robust data warehouse compatibility',
      'To power custom, native commerce experiences'
    ],
    image: '/images/products-unify.png',
    new: false,
    slug: 'unify'
  },
  {
    title: 'Optimize',
    description:
      'Optimize builds on unified revenue intelligence by tying clicks and revenue out to content, traffic sources, audiences, and offsite distribution. Expose exactly where revenue is won, lost, and ready to scale.',
    teams: [
      'Clicks and revenue for offsite channels like email, app, and syndication',
      'Conversion by website content and placement',
      'Higher value audience segments powered by buying behavior',
      'Revenue by traffic source, including LLMs',
      'Purchase behavior by subscriber or creator',
      'Growth without operational overhead'
    ],
    image: '/images/products-optimize.png',
    new: false,
    slug: 'optimize'
  },
  {
    title: 'Amplify',
    description:
      'Analyze portfolio-wide affiliate data for AI generated opportunities where incremental revenue is being left on the table. Then use our sponsorship infrastructure to automate execution at scale.',
    teams: [
      'New revenue without new inventory',
      'Optimization they control, not outsource',
      'Automated, portfolio-wide analysis',
      'Campaigns ready to activate immediately',
      'Campaign infrastructure that supports scale',
      'Automated link swaps, not manual edits',
      'Link-specific sponsorship intelligence',
      'Live insights to adjust mid-campaign'
    ],
    image: '/images/products-amplify.png',
    new: true,
    slug: 'amplify'
  }
];

export const PublisherSolutionsSection = () => {
  return (
    <SectionWrapper id="publisher-solutions">
      <SectionHeader>
        <Headline component="h2">Publisher Solutions</Headline>
        <SectionDescription>
          Drive growth across diverse monetization models and channels with a
          single platform
        </SectionDescription>
      </SectionHeader>
      <Grid>
        {solutions.map((solution) => (
          <SolutionCard key={solution.title} new={solution.new}>
            {solution.image ? (
              <img
                src={solution.image}
                alt={solution.title}
                style={{ width: '100%', height: 'auto' }}
              />
            ) : (
              <ImagePlaceholder />
            )}
            <Headline
              component="h3"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              {solution.title}
              {solution.new && <NewBadge>New</NewBadge>}
            </Headline>
            <SolutionDescription>{solution.description}</SolutionDescription>
            <PerfectFor>
              <PerfectForLabel>Built for teams who want:</PerfectForLabel>
              <TeamList>
                {solution.teams.map((team) => (
                  <TeamItem key={team}>{team}</TeamItem>
                ))}
              </TeamList>
            </PerfectFor>
            <LearnMoreLink href={CALENDAR_LINK}>
              Get a demo <ArrowRight size={18} />
            </LearnMoreLink>
          </SolutionCard>
        ))}
      </Grid>
    </SectionWrapper>
  );
};
